import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import IconButton from '~/components/buttons/NewIconButton';
import { ActionBadge } from '~/components/data-display/ActionBadge/ActionBadge';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalActions from '~/components/modals/Modal/ModalActions';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconCheck from '~/icons/icon-check.svg?react';
import IconDown from '~/icons/icon-chevron-down.svg?react';
import IconUp from '~/icons/icon-chevron-up.svg?react';
import IconLinkOn from '~/icons/icon-link-on.svg?react';
import { ModalProps } from '~/modals/Modal';
import { useSplitPayment } from '~/services/flow';
import {
  invalidateReconciliationCaches,
  useCreateReconciliationMutation,
  useDeleteReconciliationMutation,
} from '~/services/reconciliation';
import colors from '~/theme/colors.module.scss';
import { ReconciliationSource } from '~/types/reconciliation';
import { Transaction } from '~/types/transaction';
import { assertIsDefined } from '~/types/typeGuards';
import { flowAmount } from '~/utils/flow';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { formatMoney } from '~/utils/money';
import { filterVerifiedReconciliations } from '~/utils/reconciliations';
import { ReconcileModalProvider, SelectedFlow, useReconcileModalContext } from './ModalContext';
import { ReconcileSearch } from './ReconcileSearch';
import styles from './ReconcileTransactionModal.module.scss';
import { SelectedFlowsRecap } from './SelectedFlowsRecap';

const TransactionDescription = () => {
  const { t } = useTranslation('reconciliations');
  const { transaction, isReconciled } = useReconcileModalContext();

  return (
    <div className={clsx(styles.transactionSummary, { isReconciled })}>
      <div className={styles.transactionInfo}>
        <VStack gap={1} justify="space-between">
          <Text variant="caption" weight="semibold" element="span" secondaryColor uppercase>
            {t('date')}
          </Text>
          <HStack gap={2}>
            <Text variant="body2" weight="semibold" element="span" uppercase>
              {formatFromISOToDisplay(transaction.date)}
            </Text>
            <Text variant="body2" weight="regular" secondaryColor>
              {t('transaction_type.type', { context: transaction.transactionType, ns: 'transactions' })}
            </Text>
          </HStack>
        </VStack>
        <VStack gap={1} justify="space-between" className={styles.transactionInfoColumnDescription}>
          <Text variant="caption" weight="semibold" element="span" secondaryColor uppercase>
            {t('description')}
          </Text>
          <HStack gap={2}>
            {transaction.counterpartName && (
              <Text variant="body2" weight="semibold" element="span">
                {transaction.counterpartName}
              </Text>
            )}
            <Text variant="body2" weight="regular" secondaryColor>
              {transaction.cleanDescription ?? transaction.description}
            </Text>
          </HStack>
        </VStack>
        <VStack gap={1} justify="space-between">
          <Text variant="caption" weight="semibold" element="span" secondaryColor uppercase>
            {t('amount')}
          </Text>
          <Text variant="body1" weight="regular" element="span">
            {formatMoney(transaction.amount)}
          </Text>
        </VStack>
      </div>
    </div>
  );
};

const SelectedDropdownButton = ({
  children,
  open,
  onClick,
}: {
  children: ReactNode;
  open: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation('reconciliations');
  const icon = open ? IconDown : IconUp;

  return (
    <div className={styles.associatedInvoicesSection}>
      <HStack gap={1} align="center">
        <SelectedButtonCounter />
        <HStack gap={0.5} align="center">
          <Text variant="body1" weight="bold">
            {t('associated_invoices')}
          </Text>
          <IconButton onClick={onClick} Icon={icon} variant="ghost" />
        </HStack>
      </HStack>
      {open && <div className={styles.associatedInvoices}>{children}</div>}
    </div>
  );
};

const SelectedButtonCounter = () => {
  const { selectedFlows } = useReconcileModalContext();
  return (
    <ActionBadge style={{ backgroundColor: colors.colorViolet100, color: colors.colorViolet600 }}>
      {selectedFlows.length}
    </ActionBadge>
  );
};

const TotalToBeReconciled = () => {
  const { t } = useTranslation('reconciliations');
  const { remainingAmount, isReconciled, transaction, hasSelected } = useReconcileModalContext();

  if (isNil(remainingAmount)) {
    return null;
  }

  const remainingAmountLabel = !hasSelected
    ? formatMoney(remainingAmount)
    : t('partial_reconciliation.total_to_be_reconciled', {
        remainingAmount: formatMoney(remainingAmount),
        totalAmount: formatMoney(transaction.amount),
      });

  return (
    <div className={styles.totalToBeReconciled}>
      <Text variant="body2" weight="semibold">
        {t('to_reconcile')}
      </Text>
      <Text
        variant="body2"
        weight="regular"
        align="right"
        className={clsx(styles.remainingAmount, { [styles.isReconciled!]: isReconciled })}
      >
        {remainingAmountLabel}
      </Text>
      {isReconciled && <Icon component={IconCheck} size="sm" className={styles.isReconciled} />}
    </div>
  );
};

export interface ConfirmReconciliationParams {
  transaction: Transaction;
  selectedFlows: SelectedFlow[];
  source?: ReconciliationSource;
  closeModal: () => void;
}

export const useCompleteReconciliation = () => {
  const { t } = useTranslation('reconciliations');

  const { captureException } = useExceptionLogger();

  const createReconciliation = useCreateReconciliationMutation({ skipCacheInvalidation: true });
  const deleteReconciliation = useDeleteReconciliationMutation({ skipCacheInvalidation: true });
  const doSplitPayment = useSplitPayment();

  return useMutation({
    mutationFn: async ({
      transaction,
      selectedFlows,
      source = ReconciliationSource.MANUAL,
      closeModal,
    }: ConfirmReconciliationParams) => {
      const flowsToReconcile = selectedFlows.map((s) => s.flow.id);
      const reconciliationsToRemove = transaction.reconciliations?.filter((r) =>
        flowsToReconcile.includes(r.flow?.id ?? ''),
      );
      const context = 'Transaction modal';

      try {
        for (const { id: reconciliationId } of reconciliationsToRemove ?? []) {
          await deleteReconciliation.mutateAsync({ reconciliationId, context });
        }

        for (const { flow, split, remainingAmount } of selectedFlows) {
          if (split) {
            assertIsDefined(remainingAmount, 'Remaining amount is not defined');
            await doSplitPayment.mutateAsync({
              sourceFlowId: flow.id,
              actionType: 'new-flow',
              data: {
                newFlowAmount: remainingAmount,
              },
            });
          }
          await createReconciliation.mutateAsync({
            transactionId: transaction.id,
            flowId: flow.id,
            source,
            context,
          });
        }

        snack.success(t('submit_success'));
        closeModal();
      } catch (err) {
        captureException(err);
        snack.error(t('submit_error'));
      } finally {
        invalidateReconciliationCaches();
      }
    },
  });
};

const ConfirmButton = ({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (params: ConfirmReconciliationParams) => void;
}) => {
  const { t } = useTranslation('reconciliations');

  const { selectedFlows, transaction } = useReconcileModalContext();

  return (
    <Button
      onClick={() => {
        onConfirm({ transaction, selectedFlows, closeModal: onClose });
      }}
      disabled={isEmpty(selectedFlows)}
    >
      {t('confirm')}
    </Button>
  );
};

const ModalFooter = ({
  closeModal,
  onConfirm,
}: {
  closeModal: () => void;
  onConfirm: (params: ConfirmReconciliationParams) => void;
}) => {
  const { hasSelected } = useReconcileModalContext();
  const [open, setOpen] = useState<boolean>(false);

  const confirm = (
    <div className={styles.confirm}>
      <TotalToBeReconciled />
      <ConfirmButton onClose={closeModal} onConfirm={onConfirm} />
    </div>
  );

  if (!hasSelected) {
    return <div className={styles.actionsNotOpen}>{confirm}</div>;
  }

  if (!open)
    return (
      <div className={styles.actionsNotOpen}>
        <SelectedDropdownButton open={open} onClick={() => setOpen((x) => !x)}>
          <SelectedFlowsRecap />
        </SelectedDropdownButton>
        {confirm}
      </div>
    );

  return (
    <div className={styles.actionsWithAssociated}>
      <SelectedDropdownButton open={open} onClick={() => setOpen((x) => !x)}>
        <SelectedFlowsRecap />
      </SelectedDropdownButton>
      <div className={styles.actionsConfirm}>{confirm}</div>
    </div>
  );
};

const ReconcileTransactionDialog = ({
  closeModal,
  onConfirm,
}: {
  closeModal: () => void;
  onConfirm: (params: ConfirmReconciliationParams) => void;
}) => {
  const { t } = useTranslation('transactions');

  return (
    <Modal
      maxWidth="960px"
      isOpen
      className={styles.modal}
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
    >
      <ModalTitle Icon={IconLinkOn} onCloseClick={() => closeModal()}>
        {t('aside.reconcile')}
      </ModalTitle>
      <ModalContent className={styles.modalContent}>
        <TransactionDescription />
        <ReconcileSearch />
      </ModalContent>
      <ModalActions>
        <ModalFooter closeModal={() => closeModal()} onConfirm={onConfirm} />
      </ModalActions>
    </Modal>
  );
};

interface Props extends ModalProps {
  transaction: Transaction;
  onConfirm: (params: ConfirmReconciliationParams) => void;
}

export const ReconcileTransactionModal = ({ closeModal, onConfirm, transaction }: Props) => {
  const initialState = filterVerifiedReconciliations(transaction.reconciliations).map((rec) => {
    assertIsDefined(rec.flow, 'missing flow');
    return {
      flow: rec.flow,
      split: false,
      amount: flowAmount(rec.flow),
    };
  });

  return (
    <ReconcileModalProvider transaction={transaction} initialState={initialState}>
      <ReconcileTransactionDialog closeModal={() => closeModal()} onConfirm={onConfirm} />
    </ReconcileModalProvider>
  );
};
